import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import MockMap from "@/components/MockMap";
import { MagnifyingGlass, Phone, Car, Check, CircleNotch } from "@phosphor-icons/react";

const STAGES = ["confirmed", "assigned", "en_route", "arrived", "completed"];
const DRIVER_PHOTO = "https://images.pexels.com/photos/30462808/pexels-photo-30462808.jpeg?auto=compress&cs=tinysrgb&w=400";

export default function Track() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const load = async (reference) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/bookings/${reference}/track`);
      setData(data);
    } catch (e) { setError(t("not_found")); setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("ref")) load(params.get("ref")); }, []); // eslint-disable-line

  useEffect(() => {
    if (data && ref) {
      timer.current = setInterval(() => load(ref), 5000);
      return () => clearInterval(timer.current);
    }
  }, [data ? data.status : null, ref]); // eslint-disable-line

  const currentIdx = data ? STAGES.indexOf(data.status === "cancelled" ? "confirmed" : data.status) : -1;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="track-page">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-[#EF5A1E] mb-4">{t("nav_track")}</p>
      <h1 className="font-heading font-light tracking-tighter text-5xl">{t("track_title")}</h1>
      <p className="font-body text-[#5A607A] mt-4 text-lg">{t("track_sub")}</p>

      <form onSubmit={(e) => { e.preventDefault(); load(ref.toUpperCase()); }} className="flex gap-3 mt-8 max-w-lg">
        <input
          data-testid="track-ref-input"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder={t("ref_ph")}
          className="flex-1 bg-white border border-zinc-200 rounded-2xl px-4 py-3.5 font-body text-sm uppercase focus:border-[#EF5A1E] focus:ring-1 focus:ring-[#EF5A1E] focus:outline-none"
        />
        <button data-testid="track-submit-btn" className="flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-[#D2450F] transition-colors duration-300">
          {loading ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />} {t("track_btn")}
        </button>
      </form>

      {error && <p className="font-body text-sm text-[#EF4444] mt-4" data-testid="track-error">{error}</p>}

      {data && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="grid lg:grid-cols-5 gap-0 border border-zinc-200 mt-10 elev">
          {/* Left: control room */}
          <div className="lg:col-span-2 bg-white p-8 border-b lg:border-b-0 lg:border-r border-zinc-200">
            <div className="flex items-center justify-between mb-8">
              <span className="font-body text-sm text-[#EF5A1E]">{ref.toUpperCase()}</span>
              {data.status === "cancelled"
                ? <span className="font-body text-[10px] uppercase tracking-widest px-2.5 py-1 border border-[#EF4444] text-[#EF4444]" data-testid="track-status">{t("status_cancelled")}</span>
                : <span className="font-body text-[10px] uppercase tracking-widest px-2.5 py-1 bg-[#EF5A1E] text-white">{t(`status_${data.status}`)}</span>}
            </div>

            {data.status !== "cancelled" && (
              <div className="space-y-1" data-testid="status-timeline">
                {STAGES.map((s, i) => (
                  <div key={s} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 flex items-center justify-center transition-colors duration-300 ${i <= currentIdx ? "bg-[#EF5A1E]" : "bg-white border border-zinc-300"}`}>
                        {i < currentIdx ? <Check size={12} weight="bold" color="#fff" /> : i === currentIdx ? <div className="w-2 h-2 bg-white" /> : null}
                      </div>
                      {i < STAGES.length - 1 && <div className={`w-px h-9 ${i < currentIdx ? "bg-[#EF5A1E]" : "bg-zinc-200"}`} />}
                    </div>
                    <span className={`font-body text-sm pt-0.5 ${i === currentIdx ? "font-semibold" : "text-[#5A607A]"}`}>{t(`status_${s}`)}</span>
                  </div>
                ))}
              </div>
            )}

            {data.driver && data.status !== "cancelled" && (
              <div className="border border-zinc-200 bg-[#FAFAFA] mt-8 flex items-stretch" data-testid="driver-card">
                <img src={DRIVER_PHOTO} alt="Driver" className="w-24 object-cover" />
                <div className="p-4 flex-1">
                  <p className="font-body text-[10px] uppercase tracking-widest text-[#5A607A] mb-1">{t("driver")}</p>
                  <p className="font-heading font-medium text-lg tracking-tight">{data.driver.name}</p>
                  <div className="flex items-center gap-1.5 mt-1 font-body text-xs text-[#5A607A]">
                    <Car size={14} /> {data.driver.vehicle} · <span className="font-body">{data.driver.plate}</span>
                  </div>
                  <a href={`tel:${data.driver.phone}`} className="flex items-center gap-1.5 mt-2 font-body text-sm text-[#EF5A1E] hover:underline">
                    <Phone size={14} weight="fill" /> {data.driver.phone}
                  </a>
                </div>
              </div>
            )}

            {data.eta_minutes && !["cancelled", "completed"].includes(data.status) && (
              <div className="mt-8 border-t border-zinc-200 pt-6">
                <p className="font-body text-[10px] uppercase tracking-widest text-[#5A607A]">{t("eta")}</p>
                <p className="font-heading font-light text-4xl tracking-tight mt-1">{data.eta_minutes} <span className="text-lg text-[#5A607A]">{t("minutes")}</span></p>
              </div>
            )}
          </div>

          {/* Right: map */}
          <div className="lg:col-span-3 min-h-[420px]">
            <MockMap progress={data.driver_progress || 0} label={data.zone_name} showCar={!["confirmed", "cancelled"].includes(data.status)} />
          </div>
        </motion.div>
      )}
    </div>
  );
}

